
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { PasswordResetNotification } from "./PasswordResetNotification";
import { useAuthRedirectUrl } from "../hooks/useAuthRedirectUrl";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

interface PasswordResetFormProps {
  onToggleForm: () => void;
  emailSent: boolean;
  setEmailSent: (sent: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  showPasswordResetNotification?: boolean;
}

const resetSchema = z.object({
  email: z.string().email("Please enter a valid email address")
});

type ResetFormValues = z.infer<typeof resetSchema>;

export const PasswordResetForm = ({
  onToggleForm,
  emailSent,
  setEmailSent,
  error,
  setError,
  showPasswordResetNotification = false
}: PasswordResetFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [initializingReset, setInitializingReset] = useState(true);
  const redirectUrls = useAuthRedirectUrl();
  
  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: ""
    }
  });

  useEffect(() => {
    const checkRecoveryState = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const isRecovery = params.get('type') === 'recovery';
        if (isRecovery) {
          console.log("Recovery flow detected, setting password reset required flag");
          localStorage.setItem('passwordResetRequired', 'true');
        }
      } catch (e) {
        console.error("Error checking recovery state:", e);
      } finally {
        setInitializingReset(false);
      }
    };
    checkRecoveryState();
  }, []);

  const handlePasswordReset = async (values: ResetFormValues) => {
    if (!redirectUrls) {
      toast.error("Unable to determine redirect URL. Please try again.");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const redirectUrl = redirectUrls.recoveryUrl;
      console.log(`Sending password reset for ${values.email} with redirect to ${redirectUrl}`);
      
      const response = await supabase.functions.invoke("send-password-reset", {
        body: {
          email: values.email,
          redirectUrl
        }
      });
      
      if (response.error) {
        throw new Error(response.error.message || "Failed to send password reset email");
      }
      
      console.log("Password reset response:", response);
      setEmailSent(true);
      toast.success("Password reset email sent. Please check your inbox.");
    } catch (error) {
      console.error("Password reset error:", error);
      setError(error instanceof Error ? error.message : "Failed to send password reset email");
      toast.error(error instanceof Error ? error.message : "Failed to send password reset email");
    } finally {
      setIsLoading(false);
    }
  };

  if (initializingReset) {
    return <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-green-400" />
        <span className="ml-2 text-sm text-zinc-400">Preparing reset form...</span>
      </div>;
  }

  if (emailSent) {
    return <div className="space-y-4 auth-form">
        <div className="p-3 bg-green-900/30 text-green-300 rounded-md border border-green-800">
          <p className="font-medium text-sm">Password reset email sent!</p>
          <p className="text-xs mt-1">Please check your inbox and follow the link in the email.</p>
        </div>
        
        <div className="text-center">
          <Button type="button" onClick={onToggleForm} variant="outline" className="w-full h-9 bg-black/50 text-white hover:bg-zinc-900 border-zinc-700">
            Back to Sign In
          </Button>
        </div>
      </div>;
  }

  return <div className="auth-form">
      {showPasswordResetNotification && <PasswordResetNotification />}
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handlePasswordReset)} className="flex flex-col">
          <div className="flex flex-col">
            <FormField 
              control={form.control} 
              name="email" 
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <FormControl>
                    <Input 
                      id="reset-email"
                      placeholder="Your email address" 
                      type="email" 
                      autoComplete="email" 
                      disabled={isLoading}
                      className="h-9 px-2 text-white border-zinc-700 focus:border-green-500 placeholder:text-zinc-400 bg-zinc-900"
                      {...field}
                    />
                  </FormControl>
                  <div className="h-2 mt-0.5">
                    <FormMessage className="text-xs text-red-400" />
                  </div>
                </FormItem>
              )} 
            />
          </div>
          
          <Button type="submit" disabled={isLoading || !redirectUrls} className="w-full h-9 text-white bg-green-600 hover:bg-green-700 mt-2">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : "Send Reset Link"}
          </Button>
          
          <div className="text-center mt-3">
            <button 
              type="button" 
              onClick={onToggleForm} 
              className="text-sm hover:underline text-green-400 hover:text-green-300" 
              disabled={isLoading}
            >
              Back to Sign In
            </button>
          </div>
        </form>
      </Form>
    </div>;
};
